import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Platform,
  Linking,
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TextInput,
  Modal,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, limit } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { getDeployTarget } from '../config/environment';
import { db, functions } from '../config/firebase';
import { useUser } from '../components/LoginPersistence';
import { useUserFunds } from '../hooks/useUserFunds';
import { formatFundsCents, WALLET_MAX_PEER_TRANSFER_CENTS, WALLET_PEER_SEND_ONCE_MESSAGE } from '../constants/userFunds';
import {
  REFERRAL_HOLD_CENTS,
  formatReferralLink,
} from '../constants/referrals';
import {
  cancelReferralCode,
  createReferralCode,
  fetchMyActiveReferral,
} from '../utils/referralService';
import { grossUpCardChargeCents } from '../utils/stripeCardFees';
import { copyTextToClipboard } from '../utils/clipboard';
import { ManageSubscription } from './ManageSubscription';
import { palette, FONT } from '../styles/tourTheme';
import { glossyFill, glossyWebStyle, mixHex } from '../utils/calendarV2Prefs';

/** Compact Stripe wordmark — signals Connect CTAs open Stripe. */
function StripeMark({ color = '#ffffff' }) {
  return (
    <Text
      accessibilityLabel="Stripe"
      style={{
        fontWeight: '700',
        fontSize: 15,
        color,
        letterSpacing: -0.4,
        fontStyle: 'italic',
        fontFamily: Platform.OS === 'web' ? 'Helvetica Neue, Helvetica, Arial, sans-serif' : undefined,
      }}
    >
      stripe
    </Text>
  );
}

function stripeOrigin() {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return null;
}

function stripeReturnUrls() {
  const origin = stripeOrigin();
  if (!origin) return {};
  return {
    returnUrl: `${origin}/wallet?connect=return`,
    refreshUrl: `${origin}/wallet?connect=refresh`,
  };
}

function statusTone(payoutsEnabled, detailsSubmitted, currentlyDue) {
  if (payoutsEnabled) return { label: 'Ready to receive payouts', color: '#2ecc71' };
  if (detailsSubmitted && (currentlyDue?.length || 0) > 0) {
    return { label: 'Needs a bit more info', color: '#f39c12' };
  }
  if (detailsSubmitted) return { label: 'Under review', color: '#3498db' };
  return { label: 'Stripe Connect not set up yet', color: '#e67e22' };
}

function resolveConnectAccountIdFromUser(user, deployTarget) {
  if (!user) return null;
  const mode =
    deployTarget === 'production' || deployTarget === 'prod' || deployTarget === 'live'
      ? 'live'
      : 'test';
  if (mode === 'test') {
    return user.stripeConnectAccountIdTest || user.stripeConnectAccountId || null;
  }
  return user.stripeConnectAccountId || user.stripeConnectAccountIdLive || null;
}

function statusFromUserDoc(user, deployTarget) {
  const accountId = resolveConnectAccountIdFromUser(user, deployTarget);
  return {
    accountId,
    detailsSubmitted: Boolean(user?.stripeConnectDetailsSubmitted),
    chargesEnabled: Boolean(user?.stripeConnectChargesEnabled),
    payoutsEnabled: Boolean(user?.stripeConnectPayoutsEnabled),
    currentlyDue: Array.isArray(user?.stripeConnectCurrentlyDue)
      ? user.stripeConnectCurrentlyDue
      : [],
    eventuallyDue: [],
    disabledReason: user?.stripeConnectDisabledReason || null,
    mode:
      deployTarget === 'production' || deployTarget === 'prod' || deployTarget === 'live'
        ? 'live'
        : 'test',
    fromUserDoc: true,
  };
}

function connectFieldsUnchanged(prev, payload) {
  if (!prev || !payload) return false;
  const mode = payload.mode || prev.stripeConnectMode;
  const accountId = payload.accountId ?? prev.stripeConnectAccountId;
  const currentlyDue = payload.currentlyDue ?? prev.stripeConnectCurrentlyDue;
  const dueSame =
    JSON.stringify(Array.isArray(currentlyDue) ? currentlyDue : []) ===
    JSON.stringify(
      Array.isArray(prev.stripeConnectCurrentlyDue) ? prev.stripeConnectCurrentlyDue : []
    );

  return (
    prev.stripeConnectAccountId === accountId &&
    (mode !== 'test' || prev.stripeConnectAccountIdTest === accountId) &&
    prev.stripeConnectMode === mode &&
    prev.stripeConnectDetailsSubmitted ===
      (payload.detailsSubmitted ?? prev.stripeConnectDetailsSubmitted) &&
    prev.stripeConnectChargesEnabled ===
      (payload.chargesEnabled ?? prev.stripeConnectChargesEnabled) &&
    prev.stripeConnectPayoutsEnabled ===
      (payload.payoutsEnabled ?? prev.stripeConnectPayoutsEnabled) &&
    dueSame
  );
}

function payoutHistoryLabel(row) {
  const t = String(row?.type || '');
  if (row?.label) return row.label;
  if (t === 'earn_in') return 'Earn credit';
  if (t === 'topup_in') return 'Card top-up';
  if (t === 'disburse_in') return row.tourName ? `Tour disburse · ${row.tourName}` : 'Tour disburse';
  if (t === 'donate_out') return 'Donate';
  if (t === 'withdraw_out') return 'Paid to Stripe Connect';
  if (t === 'transfer_in') return 'Transfer received';
  if (t === 'transfer_out') return 'Transfer sent';
  return t || 'Transaction';
}

function payoutHistorySignedCents(row) {
  const amount = Math.max(0, Number(row?.amountCents) || 0);
  const t = String(row?.type || '');
  if (t === 'earn_in' || t === 'disburse_in' || t === 'transfer_in' || t === 'topup_in') return amount;
  if (t === 'donate_out' || t === 'withdraw_out' || t === 'transfer_out') return -amount;
  return amount;
}

export function Balance({ navigation, route }) {
  const hubTabFromRoute =
    route?.params?.tab === 'membership' ? 'membership' : 'wallet';
  const [hubTab, setHubTab] = useState(hubTabFromRoute);

  useEffect(() => {
    if (route?.params?.tab === 'membership' || route?.params?.tab === 'wallet') {
      setHubTab(route.params.tab);
    }
  }, [route?.params?.tab]);
  const { isDarkMode } = useTheme();
  const { user, setUser } = useUser();
  const userRef = useRef(user);
  userRef.current = user;

  const {
    payoutBalanceCents,
    payoutBalanceReferralHoldCents,
    payoutWithdrawnCents,
    loading: fundsLoading,
    actionBusy: fundsActionBusy,
    transferToUser,
    lookupUserByEmail,
    hasSentWalletTo,
  } = useUserFunds(user?.uid);

  const [activeReferrals, setActiveReferrals] = useState([]);
  const [referralBusy, setReferralBusy] = useState(false);
  const [referralMsg, setReferralMsg] = useState('');
  const [copiedReferralCode, setCopiedReferralCode] = useState('');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [successNote, setSuccessNote] = useState('');

  const [payoutHistory, setPayoutHistory] = useState([]);
  const [withdrawDollars, setWithdrawDollars] = useState('');
  const [addBalanceDollars, setAddBalanceDollars] = useState('10');
  const [addOpen, setAddOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawStep, setWithdrawStep] = useState('amount'); // 'amount' | 'confirm'
  const [withdrawModalError, setWithdrawModalError] = useState('');
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sendDollars, setSendDollars] = useState('');
  const [sendRecipient, setSendRecipient] = useState(null);
  const [sendModalError, setSendModalError] = useState('');

  const pal = palette(isDarkMode);
  const accent = pal.teal;
  const gloss = glossyFill(accent, { isDarkMode, role: 'panel' });
  const cta = glossyFill(mixHex(accent, '#000000', 0.18), { isDarkMode: true, role: 'chip', selected: true });

  const deployTarget = getDeployTarget();
  const uid = user?.uid || null;

  const addBalancePreview = useMemo(() => {
    const dollars = Number(String(addBalanceDollars).replace(/[^0-9.]/g, ''));
    const netCents = Math.round(dollars * 100);
    if (!Number.isFinite(netCents) || netCents < 100) return null;
    return grossUpCardChargeCents(netCents);
  }, [addBalanceDollars]);

  const spendableWalletCents = useMemo(
    () => Math.max(0, payoutBalanceCents - payoutBalanceReferralHoldCents),
    [payoutBalanceCents, payoutBalanceReferralHoldCents],
  );

  const loadActiveReferral = useCallback(async () => {
    if (!uid) {
      setActiveReferrals([]);
      return;
    }
    try {
      const data = await fetchMyActiveReferral();
      setActiveReferrals(Array.isArray(data?.referrals) ? data.referrals : []);
    } catch (_) {
      setActiveReferrals([]);
    }
  }, [uid]);

  useEffect(() => {
    loadActiveReferral();
  }, [loadActiveReferral]);

  const handleCopyReferralLink = useCallback(async (code) => {
    const link = formatReferralLink(stripeOrigin(), code);
    if (!link) {
      setError('Could not build referral link.');
      return;
    }
    try {
      await copyTextToClipboard(link);
      setCopiedReferralCode(code);
      setReferralMsg(`Link copied — share ${code} with a friend.`);
      setTimeout(() => {
        setCopiedReferralCode((prev) => (prev === code ? '' : prev));
      }, 2500);
    } catch (err) {
      setError(err?.message || 'Could not copy link.');
    }
  }, []);

  const handleCreateReferral = async () => {
    setReferralMsg('');
    setError('');
    setReferralBusy(true);
    try {
      const data = await createReferralCode();
      await loadActiveReferral();
      const code = data?.code;
      if (code) {
        const link = formatReferralLink(stripeOrigin(), code);
        if (link) {
          await copyTextToClipboard(link);
          setCopiedReferralCode(code);
          setReferralMsg(`Referral ${code} created — link copied to clipboard. ${formatFundsCents(REFERRAL_HOLD_CENTS)} on hold.`);
          setTimeout(() => {
            setCopiedReferralCode((prev) => (prev === code ? '' : prev));
          }, 3000);
        } else {
          setReferralMsg(`Referral ${code} created — ${formatFundsCents(REFERRAL_HOLD_CENTS)} on hold.`);
        }
      }
    } catch (err) {
      setError(err?.message || 'Could not create referral.');
    } finally {
      setReferralBusy(false);
    }
  };

  const handleCancelReferral = async (code) => {
    if (!code) return;
    setReferralMsg('');
    setError('');
    setReferralBusy(true);
    try {
      await cancelReferralCode(code);
      setReferralMsg(`Referral ${code} cancelled — $22 returned to your Wallet.`);
      await loadActiveReferral();
    } catch (err) {
      setError(err?.message || 'Could not cancel referral.');
    } finally {
      setReferralBusy(false);
    }
  };

  const canCreateReferral =
    spendableWalletCents >= REFERRAL_HOLD_CENTS && !referralBusy && !fundsLoading && !fundsActionBusy;
  const accountId = useMemo(
    () => resolveConnectAccountIdFromUser(user, deployTarget),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      user?.stripeConnectAccountId,
      user?.stripeConnectAccountIdTest,
      user?.stripeConnectAccountIdLive,
      deployTarget,
    ]
  );

  const syncUserConnectFields = useCallback((payload) => {
    if (!payload || !setUser) return;
    setUser((prev) => {
      if (!prev) return prev;
      if (connectFieldsUnchanged(prev, payload)) return prev;

      const mode = payload.mode || prev.stripeConnectMode;
      const next = {
        ...prev,
        stripeConnectAccountId: payload.accountId ?? prev.stripeConnectAccountId,
        stripeConnectMode: mode ?? prev.stripeConnectMode,
        stripeConnectDetailsSubmitted:
          payload.detailsSubmitted ?? prev.stripeConnectDetailsSubmitted,
        stripeConnectChargesEnabled:
          payload.chargesEnabled ?? prev.stripeConnectChargesEnabled,
        stripeConnectPayoutsEnabled:
          payload.payoutsEnabled ?? prev.stripeConnectPayoutsEnabled,
        stripeConnectCurrentlyDue:
          payload.currentlyDue ?? prev.stripeConnectCurrentlyDue,
      };
      if (mode === 'test' && payload.accountId) {
        next.stripeConnectAccountIdTest = payload.accountId;
      }
      return next;
    });
  }, [setUser]);

  const loadStatus = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    const currentAccountId = resolveConnectAccountIdFromUser(currentUser, deployTarget);
    if (!currentAccountId) {
      setStatus(statusFromUserDoc(currentUser, deployTarget));
      setError('');
      setLoading(false);
      return;
    }

    setError('');
    setLoading(true);
    try {
      const getConnectAccountStatus = httpsCallable(functions, 'getConnectAccountStatus', {
        timeout: 60000,
      });
      const result = await getConnectAccountStatus({ deployTarget });
      const payload = result?.data || null;
      setStatus(payload);
      syncUserConnectFields(payload);
    } catch (err) {
      console.error('Stripe status load error:', err);
      setStatus(statusFromUserDoc(userRef.current, deployTarget));
      setError(err?.message || 'Could not refresh payout status from Stripe.');
    } finally {
      setLoading(false);
    }
  }, [deployTarget, syncUserConnectFields]);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadStatus();
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, accountId, loadStatus]);

  useEffect(() => {
    if (!uid) {
      setPayoutHistory([]);
      return undefined;
    }
    const unsub = onSnapshot(
      query(collection(db, 'users', uid, 'payout_history'), limit(60)),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        rows.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() || Number(a.createdAt) || 0;
          const tb = b.createdAt?.toMillis?.() || Number(b.createdAt) || 0;
          return tb - ta;
        });
        setPayoutHistory(rows.slice(0, 40));
      },
      (err) => console.warn('[Balance] payout_history', err)
    );
    return () => unsub?.();
  }, [uid]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('balance') === 'added') {
      setSuccessNote('Card payment received — Wallet updates when the webhook lands.');
    } else if (params.get('balance') === 'cancelled') {
      setSuccessNote('Wallet top-up cancelled — no charge was made.');
    }
    if (params.get('connect') === 'return') {
      setSuccessNote('Returned from Stripe Connect — refreshing payout status.');
      loadStatus();
    }
  }, [loadStatus]);

  const runAction = async (key, fn) => {
    setError('');
    setSuccessNote('');
    setActionLoading(key);
    try {
      await fn();
    } catch (err) {
      console.error(`Stripe action ${key} failed:`, err);
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartOnboarding = () =>
    runAction('onboard', async () => {
      const createConnectAccount = httpsCallable(functions, 'createConnectAccount');
      const createConnectAccountLink = httpsCallable(functions, 'createConnectAccountLink');

      const accountResult = await createConnectAccount({ deployTarget });
      syncUserConnectFields(accountResult?.data);
      setStatus((prev) => ({ ...(prev || {}), ...(accountResult?.data || {}) }));

      const linkResult = await createConnectAccountLink({
        deployTarget,
        ...stripeReturnUrls(),
      });
      const url = linkResult?.data?.url;
      if (!url) throw new Error('Onboarding URL was not returned.');

      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    });

  const handleContinueOnboarding = () =>
    runAction('continue', async () => {
      let id = status?.accountId;
      if (!id) {
        const createConnectAccount = httpsCallable(functions, 'createConnectAccount');
        const accountResult = await createConnectAccount({ deployTarget });
        id = accountResult?.data?.accountId;
        syncUserConnectFields(accountResult?.data);
        setStatus((prev) => ({ ...(prev || {}), ...(accountResult?.data || {}) }));
      }

      const createConnectAccountLink = httpsCallable(functions, 'createConnectAccountLink');
      const linkResult = await createConnectAccountLink({
        deployTarget,
        ...stripeReturnUrls(),
      });
      const url = linkResult?.data?.url;
      if (!url) throw new Error('Onboarding URL was not returned.');

      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    });

  const handleOpenDashboard = () =>
    runAction('dashboard', async () => {
      const createConnectLoginLink = httpsCallable(functions, 'createConnectLoginLink');
      const result = await createConnectLoginLink({ deployTarget });
      const url = result?.data?.url;
      if (!url) throw new Error('Dashboard URL was not returned.');
      if (Platform.OS === 'web') {
        window.open(url, '_blank');
      } else {
        await Linking.openURL(url);
      }
    });

  const handleAddBalance = () =>
    runAction('addBalance', async () => {
      const dollars = Number(String(addBalanceDollars).replace(/[^0-9.]/g, ''));
      const amountCents = Math.round(dollars * 100);
      if (!Number.isFinite(amountCents) || amountCents < 100) {
        throw new Error('Enter at least $1.00');
      }
      const origin = stripeOrigin();
      const createVrcBalanceTopUpCheckout = httpsCallable(
        functions,
        'createVrcBalanceTopUpCheckout',
        { timeout: 60000 }
      );
      const result = await createVrcBalanceTopUpCheckout({
        amountCents,
        deployTarget,
        ...(origin
          ? {
              successUrl: `${origin}/wallet?balance=added`,
              cancelUrl: `${origin}/wallet?balance=cancelled`,
            }
          : {}),
      });
      const url = result?.data?.url;
      if (!url) throw new Error('Checkout URL was not returned.');
      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    });

  const openWithdrawModal = () => {
    setError('');
    setSuccessNote('');
    setWithdrawModalError('');
    if (!canReceive) {
      setError('Finish payout setup before withdrawing.');
      return;
    }
    if (payoutBalanceCents < 100) {
      setError('You need at least $1.00 in Wallet to withdraw.');
      return;
    }
    setAddOpen(false);
    setWithdrawDollars((payoutBalanceCents / 100).toFixed(2));
    setWithdrawStep('amount');
    setWithdrawModalOpen(true);
  };

  const closeWithdrawModal = () => {
    if (actionLoading === 'withdraw') return;
    setWithdrawModalOpen(false);
    setWithdrawStep('amount');
    setWithdrawModalError('');
  };

  const openSendModal = () => {
    if (!uid) {
      navigation.navigate('Login');
      return;
    }
    setError('');
    setSendModalError('');
    setSendEmail('');
    setSendDollars('');
    setSendRecipient(null);
    setAddOpen(false);
    setSendModalOpen(true);
  };

  const closeSendModal = () => {
    if (actionLoading === 'send') return;
    setSendModalOpen(false);
    setSendModalError('');
  };

  const parseSendCents = () => {
    const dollars = Number(String(sendDollars || '').replace(/[^0-9.]/g, ''));
    return Math.round(dollars * 100);
  };

  const handleFindSendRecipient = async () => {
    setSendModalError('');
    const email = String(sendEmail || '').trim();
    if (!email.includes('@')) {
      setSendModalError('Enter a valid email address.');
      return;
    }
    try {
      const found = await lookupUserByEmail(email);
      if (!found) {
        setSendModalError('No VRC user found with that email.');
        setSendRecipient(null);
        return;
      }
      if (found.uid === uid) {
        setSendModalError('You cannot send Wallet funds to yourself.');
        setSendRecipient(null);
        return;
      }
      if (await hasSentWalletTo(found.uid)) {
        setSendModalError(WALLET_PEER_SEND_ONCE_MESSAGE);
        setSendRecipient(null);
        return;
      }
      setSendRecipient(found);
    } catch (err) {
      setSendModalError(err?.message || 'Could not look up that user.');
      setSendRecipient(null);
    }
  };

  const handleSendToUser = () =>
    runAction('send', async () => {
      if (!sendRecipient?.uid) {
        throw new Error('Find a member by email first.');
      }
      const amountCents = parseSendCents();
      if (!Number.isFinite(amountCents) || amountCents < 100) {
        throw new Error('Enter at least $1.00 to send.');
      }
      if (amountCents > spendableWalletCents) {
        throw new Error('Amount exceeds spendable Wallet balance (referral hold cannot be sent).');
      }
      if (amountCents > WALLET_MAX_PEER_TRANSFER_CENTS) {
        throw new Error(
          `Member sends are limited to ${formatFundsCents(WALLET_MAX_PEER_TRANSFER_CENTS)} per transfer. `
          + 'Use a tour fund disbursement for larger amounts.',
        );
      }
      await transferToUser(sendRecipient.uid, amountCents, sendRecipient.displayName);
      setSendModalOpen(false);
      setSendEmail('');
      setSendDollars('');
      setSendRecipient(null);
      setSuccessNote(
        `Sent ${formatFundsCents(amountCents)} to ${sendRecipient.displayName}.`,
      );
    });

  const parseWithdrawCents = () => {
    const dollars = Number(String(withdrawDollars || '').replace(/[^0-9.]/g, ''));
    return Math.round(dollars * 100);
  };

  const goWithdrawConfirm = () => {
    setWithdrawModalError('');
    const amountCents = parseWithdrawCents();
    if (!Number.isFinite(amountCents) || amountCents < 100) {
      setWithdrawModalError('Enter at least $1.00 to withdraw.');
      return;
    }
    if (amountCents > payoutBalanceCents) {
      setWithdrawModalError('Amount exceeds your Wallet balance.');
      return;
    }
    setWithdrawStep('confirm');
  };

  const handleWithdrawToConnect = () =>
    runAction('withdraw', async () => {
      const amountCents = parseWithdrawCents();
      if (!Number.isFinite(amountCents) || amountCents < 100) {
        throw new Error('Enter at least $1.00 to withdraw');
      }
      if (amountCents > payoutBalanceCents) {
          throw new Error('Amount exceeds your Wallet balance');
      }
      const withdrawPayoutBalanceToConnect = httpsCallable(
        functions,
        'withdrawPayoutBalanceToConnect',
        { timeout: 90000 }
      );
      const result = await withdrawPayoutBalanceToConnect({
        amountCents,
        deployTarget,
      });
      setWithdrawDollars('');
      setWithdrawModalOpen(false);
      setWithdrawStep('amount');
      setWithdrawModalError('');
      setSuccessNote(
        `Sent ${formatFundsCents(amountCents)} to your payout account — transfer ${
          result?.data?.transferId || 'ok'
        }`
      );
    });

  const tone = useMemo(
    () =>
      statusTone(
        status?.payoutsEnabled,
        status?.detailsSubmitted,
        status?.currentlyDue
      ),
    [status]
  );

  const hasAccount = Boolean(status?.accountId);
  const canReceive = Boolean(status?.payoutsEnabled);
  const needsMoreInfo =
    hasAccount && !canReceive && ((status?.currentlyDue?.length || 0) > 0 || !status?.detailsSubmitted);

  const connectCta = !hasAccount
    ? { key: 'onboard', label: 'Set up payouts', onPress: handleStartOnboarding }
    : needsMoreInfo
      ? { key: 'continue', label: 'Continue setup', onPress: handleContinueOnboarding }
      : canReceive
        ? { key: 'dashboard', label: 'View payouts', onPress: handleOpenDashboard }
        : null;

  return (
    <View style={[styles.page, { backgroundColor: pal.page }]}>
      <ScrollView contentContainerStyle={styles.pageInner}>
        {error ? (
          <View style={[styles.banner, { backgroundColor: pal.alert }]}>
            <Text style={{ color: '#fff', fontSize: 14.5 }}>{error}</Text>
          </View>
        ) : null}
        {successNote ? (
          <View style={[styles.banner, { backgroundColor: pal.ok }]}>
            <Text style={{ color: '#fff', fontSize: 14.5 }}>{successNote}</Text>
          </View>
        ) : null}

        <View style={[styles.hubTabs, { borderColor: pal.line }]}>
          <TouchableOpacity
            onPress={() => setHubTab('wallet')}
            style={[
              styles.hubTab,
              hubTab === 'wallet' && { borderBottomColor: accent, borderBottomWidth: 2 },
            ]}
          >
            <Text
              style={{
                color: hubTab === 'wallet' ? pal.ink : pal.mute,
                fontWeight: hubTab === 'wallet' ? '700' : '500',
                fontSize: 15,
              }}
            >
              Wallet
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setHubTab('membership')}
            style={[
              styles.hubTab,
              hubTab === 'membership' && { borderBottomColor: accent, borderBottomWidth: 2 },
            ]}
          >
            <Text
              style={{
                color: hubTab === 'membership' ? pal.ink : pal.mute,
                fontWeight: hubTab === 'membership' ? '700' : '500',
                fontSize: 15,
              }}
            >
              Membership & Credits
            </Text>
          </TouchableOpacity>
        </View>

        {hubTab === 'membership' ? (
          <ManageSubscription navigation={navigation} embedded />
        ) : (
          <>
        <View style={[styles.heroRow, { borderBottomColor: accent }]}>
          <View style={{ flex: 1.2, minWidth: 240 }}>
            <Text style={[s.label, { color: pal.mute, marginBottom: 6 }]}>Payments</Text>
            <Text style={[s.hero, { color: pal.ink, fontFamily: FONT.sans }]}>Your Wallet</Text>
            <Text style={{ color: pal.mute, marginTop: 8, fontSize: 15, lineHeight: 22, maxWidth: 420 }}>
              Spend Wallet on mutual funds, tour funds, member sends, and referrals. Withdraw to Stripe Connect when set up.
            </Text>
          </View>

          <View
            style={[
              styles.accentPanel,
              {
                borderColor: gloss.borderColor || pal.line,
                borderLeftColor: accent,
                ...(Platform.OS === 'web'
                  ? glossyWebStyle(gloss)
                  : { backgroundColor: gloss.backgroundColor }),
              },
            ]}
          >
            <Text style={[s.label, { color: gloss.mute, marginBottom: 10 }]}>Wallet</Text>

            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ fontSize: 36, fontWeight: '600', color: gloss.ink, letterSpacing: -0.8 }}>
                {fundsLoading ? '…' : formatFundsCents(payoutBalanceCents)}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  setAddOpen((v) => !v);
                  if (!addOpen) setSendModalOpen(false);
                }}
                disabled={Boolean(actionLoading)}
                  accessibilityLabel="Add to Wallet"
                style={[
                  styles.plusBtn,
                  {
                    borderColor: gloss.ink,
                    backgroundColor: addOpen ? gloss.ink : 'transparent',
                    opacity: actionLoading ? 0.55 : 1,
                  },
                ]}
              >
                <Ionicons
                  name="add"
                  size={20}
                  color={addOpen ? (gloss.lightType ? '#14141a' : '#f4f4f2') : gloss.ink}
                />
              </TouchableOpacity>
            </View>
            <Text style={{ color: gloss.mute, fontSize: 13.5, marginTop: 4 }}>
              Available to spend or withdraw
              {payoutBalanceReferralHoldCents > 0
                ? ` · ${formatFundsCents(payoutBalanceReferralHoldCents)} on hold for referral`
                : ''}
            </Text>

            {addOpen ? (
              <View style={[styles.inlinePanel, { borderTopColor: gloss.line }]}>
                <Text style={[s.label, { color: gloss.mute, marginBottom: 6 }]}>Add by card ($)</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <TextInput
                    value={addBalanceDollars}
                    onChangeText={setAddBalanceDollars}
                    keyboardType="decimal-pad"
                    placeholder="10.00"
                    placeholderTextColor={gloss.mute}
                    style={[
                      styles.inlineInput,
                      {
                        borderColor: gloss.line,
                        backgroundColor: gloss.well || pal.sunk,
                        color: gloss.ink,
                      },
                    ]}
                  />
                  <TouchableOpacity
                    onPress={handleAddBalance}
                    disabled={Boolean(actionLoading)}
                    style={[
                      styles.compactBtn,
                      {
                        backgroundColor: gloss.ink,
                        opacity: actionLoading ? 0.55 : 1,
                      },
                    ]}
                  >
                    {actionLoading === 'addBalance' ? (
                      <ActivityIndicator color={gloss.page || '#fff'} />
                    ) : (
                      <Text style={{ color: gloss.page || pal.page, fontWeight: '600' }}>Pay</Text>
                    )}
                  </TouchableOpacity>
                </View>
                {addBalancePreview ? (
                  <Text style={{ color: gloss.mute, fontSize: 13, marginTop: 10, lineHeight: 19 }}>
                    You receive {formatFundsCents(addBalancePreview.netCents)} in Wallet.{' '}
                    Processing fee {formatFundsCents(addBalancePreview.processingFeeCents)} — card total{' '}
                    {formatFundsCents(addBalancePreview.chargeCents)}.
                  </Text>
                ) : null}
              </View>
            ) : null}

            <View style={[styles.accentLine, { borderTopColor: gloss.line, marginTop: 14 }]}>
              <Text style={{ color: gloss.mute, fontSize: 15 }}>Paid out to Stripe</Text>
              <Text style={{ color: gloss.ink, fontSize: 15, fontFamily: FONT.mono }}>
                {fundsLoading ? '…' : formatFundsCents(payoutWithdrawnCents)}
              </Text>
            </View>

            <TouchableOpacity
              onPress={openSendModal}
              disabled={Boolean(actionLoading) || fundsLoading}
              style={[
                s.outlineBtn,
                {
                  borderColor: gloss.ink,
                  alignSelf: 'stretch',
                  marginTop: 14,
                  opacity: actionLoading || fundsLoading ? 0.6 : 1,
                },
              ]}
            >
              <Text style={{ color: gloss.ink, fontWeight: '600', textAlign: 'center', fontSize: 14.5 }}>
                Send to a member
              </Text>
            </TouchableOpacity>

            {/* Withdraw — opens confirmation popup */}
            <TouchableOpacity
              onPress={openWithdrawModal}
              disabled={Boolean(actionLoading)}
              style={[
                s.outlineBtn,
                {
                  borderColor: gloss.line,
                  alignSelf: 'stretch',
                  marginTop: 14,
                  opacity: actionLoading ? 0.6 : 1,
                },
              ]}
            >
              <Text style={[s.kicker, { color: gloss.mute, textAlign: 'center' }]}>
                Withdraw to Stripe Connect
              </Text>
            </TouchableOpacity>

            <View style={[styles.accentStatusRow, { marginTop: 16 }]}>
              <View style={[styles.statusDot, { backgroundColor: tone.color }]} />
              <Text style={{ color: gloss.ink, fontWeight: '600', fontSize: 14.5, flex: 1 }}>
                {loading ? 'Checking payouts…' : tone.label}
              </Text>
            </View>

            {status?.accountId ? (
              <Text style={[s.meta, { color: gloss.mute, marginTop: 8 }]} numberOfLines={1}>
                {status.accountId}
              </Text>
            ) : null}

            <View style={{ marginTop: 14, gap: 8 }}>
              {connectCta ? (
                <TouchableOpacity
                  onPress={connectCta.onPress}
                  disabled={Boolean(actionLoading)}
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: 14,
                    borderRadius: 9,
                    overflow: 'hidden',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    ...(Platform.OS === 'web'
                      ? glossyWebStyle(cta)
                      : { backgroundColor: cta.backgroundColor }),
                    opacity: actionLoading ? 0.6 : 1,
                  }}
                >
                  {actionLoading === connectCta.key ? (
                    <ActivityIndicator color={cta.ink} />
                  ) : (
                    <>
                      <StripeMark color="#ffffff" />
                      <Text style={{ fontWeight: '600', color: cta.ink }}>{connectCta.label}</Text>
                    </>
                  )}
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity
                onPress={loadStatus}
                disabled={Boolean(actionLoading) || loading}
                style={[s.outlineBtn, { borderColor: gloss.line, alignSelf: 'stretch' }]}
              >
                <Text style={[s.kicker, { color: gloss.mute, textAlign: 'center' }]}>
                  Refresh status
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
          <Text style={[s.kicker, { color: pal.mute, marginBottom: 8 }]}>Member referral</Text>
          <Text style={{ color: pal.ink, fontSize: 15, lineHeight: 22 }}>
            Put {formatFundsCents(REFERRAL_HOLD_CENTS)} from your Wallet on hold for each link. A friend gets that amount off their first
            month when they subscribe. Cancel anytime before they join to release the hold. You can run several referrals at once.
          </Text>
          {referralMsg ? (
            <Text style={{ color: pal.ok, marginTop: 10, fontSize: 14 }}>{referralMsg}</Text>
          ) : null}
          {activeReferrals.length > 0 ? (
            <View style={{ marginTop: 14, gap: 14 }}>
              <Text style={[s.label, { color: pal.mute }]}>Active referrals ({activeReferrals.length})</Text>
              {activeReferrals.map((ref) => {
                const link = formatReferralLink(stripeOrigin(), ref.code);
                const justCopied = copiedReferralCode === ref.code;
                return (
                  <View
                    key={ref.code}
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: pal.line,
                      gap: 8,
                    }}
                  >
                    <Text style={{ color: pal.ink, fontWeight: '600' }}>Code: {ref.code}</Text>
                    {link ? (
                      <>
                        <TextInput
                          value={link}
                          editable={false}
                          selectTextOnFocus
                          multiline
                          style={{
                            color: pal.ink,
                            fontSize: 13.5,
                            paddingVertical: 10,
                            paddingHorizontal: 12,
                            borderWidth: 1,
                            borderColor: pal.line,
                            borderRadius: 8,
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                            ...(Platform.OS === 'web' ? { cursor: 'text', userSelect: 'text' } : {}),
                          }}
                        />
                        <TouchableOpacity
                          onPress={() => handleCopyReferralLink(ref.code)}
                          style={[
                            styles.compactBtn,
                            {
                              backgroundColor: justCopied ? pal.ok : accent,
                              alignSelf: 'stretch',
                              flexDirection: 'row',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 8,
                            },
                          ]}
                        >
                          <Ionicons name={justCopied ? 'checkmark-circle' : 'copy-outline'} size={18} color="#fff" />
                          <Text style={{ color: '#fff', fontWeight: '600' }}>
                            {justCopied ? 'Copied!' : 'Copy referral link'}
                          </Text>
                        </TouchableOpacity>
                      </>
                    ) : null}
                    <TouchableOpacity
                      onPress={() => handleCancelReferral(ref.code)}
                      disabled={referralBusy}
                      style={[s.outlineBtn, { borderColor: pal.alert, alignSelf: 'flex-start' }]}
                    >
                      {referralBusy ? (
                        <ActivityIndicator color={pal.alert} />
                      ) : (
                        <Text style={{ color: pal.alert, fontWeight: '600' }}>
                          Cancel {ref.code} & release {formatFundsCents(REFERRAL_HOLD_CENTS)}
                        </Text>
                      )}
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>
          ) : null}
          <TouchableOpacity
            onPress={handleCreateReferral}
            disabled={!canCreateReferral}
            style={[
              styles.compactBtn,
              {
                backgroundColor: accent,
                marginTop: 14,
                alignSelf: 'flex-start',
                opacity: canCreateReferral ? 1 : 0.5,
              },
            ]}
          >
            {referralBusy ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={{ color: '#fff', fontWeight: '600' }}>
                {activeReferrals.length > 0 ? 'Create another referral' : 'Create referral'} (
                {formatFundsCents(REFERRAL_HOLD_CENTS)} hold)
              </Text>
            )}
          </TouchableOpacity>
        </View>

        {/* Wallet ledger */}
        <View
          style={[
            s.card,
            { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' },
          ]}
        >
          <View style={styles.tableHead}>
            <Text style={[s.label, { color: pal.mute }]}>Wallet ledger</Text>
            <Text style={{ color: pal.mute, fontSize: 13.5 }}>
              {payoutHistory.length} transaction{payoutHistory.length === 1 ? '' : 's'}
            </Text>
          </View>
          <View style={[styles.tierHead, { borderTopColor: pal.lineSoft }]}>
            <Text style={[s.colH, { color: pal.mute, flex: 1.6 }]}>Transaction</Text>
            <Text style={[s.colH, { color: pal.mute, width: 100, textAlign: 'right' }]}>Amount</Text>
          </View>
          {payoutHistory.length === 0 ? (
            <Text style={{ color: pal.mute, padding: 18 }}>
              No balance movements yet. Add by card, earn credits, or receive a tour disburse.
            </Text>
          ) : (
            payoutHistory.map((row) => {
              const signed = payoutHistorySignedCents(row);
              const amountColor = signed < 0 ? pal.alert || '#c0392b' : pal.ink;
              return (
                <View key={row.id} style={[styles.tierRow, { borderTopColor: pal.lineSoft }]}>
                  <View style={{ flex: 1.6 }}>
                    <Text style={{ color: pal.ink, fontSize: 14.5 }} numberOfLines={2}>
                      {payoutHistoryLabel(row)}
                    </Text>
                    {row.stripeTransferId || row.tourId ? (
                      <Text
                        style={{
                          color: pal.mute,
                          fontFamily: FONT.mono,
                          fontSize: 11.5,
                          marginTop: 2,
                        }}
                        numberOfLines={1}
                      >
                        {row.stripeTransferId || row.tourId}
                      </Text>
                    ) : null}
                  </View>
                  <Text
                    style={{
                      width: 100,
                      textAlign: 'right',
                      color: amountColor,
                      fontFamily: FONT.mono,
                      fontSize: 14,
                    }}
                  >
                    {signed < 0 ? '−' : '+'}
                    {formatFundsCents(Math.abs(signed))}
                  </Text>
                </View>
              );
            })
          )}
        </View>

        <TouchableOpacity
          style={{ marginTop: 4, marginBottom: 32, alignSelf: 'flex-start' }}
          onPress={() => navigation.navigate('MutualAidFund')}
        >
          <Text style={[s.kicker, { color: pal.link }]}>Back to Fund</Text>
        </TouchableOpacity>
          </>
        )}
      </ScrollView>

      <Modal
        visible={sendModalOpen}
        transparent
        animationType="fade"
        onRequestClose={closeSendModal}
      >
        <Pressable style={styles.modalBackdrop} onPress={closeSendModal}>
          <Pressable
            style={[styles.modalSheet, { backgroundColor: pal.page, borderColor: pal.line, borderLeftColor: accent }]}
            onPress={(e) => e.stopPropagation?.()}
          >
            <View style={[styles.modalHeader, { borderBottomColor: accent }]}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={[s.label, { color: pal.mute, marginBottom: 4 }]}>Wallet</Text>
                <Text style={[styles.modalTitle, { color: pal.ink }]}>Send to a member</Text>
              </View>
              <TouchableOpacity
                onPress={closeSendModal}
                disabled={actionLoading === 'send'}
                style={[styles.modalClose, { borderColor: pal.line }]}
              >
                <Ionicons name="close" size={18} color={pal.ink} />
              </TouchableOpacity>
            </View>

            {sendModalError ? (
              <View style={[styles.banner, { backgroundColor: pal.alert, marginBottom: 12 }]}>
                <Text style={{ color: '#fff', fontSize: 14 }}>{sendModalError}</Text>
              </View>
            ) : null}

            <Text style={{ color: pal.mute, fontSize: 15, lineHeight: 22, marginBottom: 12 }}>
              Transfer from your Wallet to another VRC account by email. Credits cannot be sent — Wallet only.
              Maximum {formatFundsCents(WALLET_MAX_PEER_TRANSFER_CENTS)} per send, and only one send per member
              (tour fund disbursements can be higher and repeat).
            </Text>
            <Text style={[s.label, { color: pal.mute, marginBottom: 8 }]}>
              Spendable {formatFundsCents(spendableWalletCents)}
            </Text>

            <TextInput
              value={sendEmail}
              onChangeText={(t) => {
                setSendModalError('');
                setSendEmail(t);
                setSendRecipient(null);
              }}
              placeholder="member@email.com"
              placeholderTextColor={pal.faint}
              autoCapitalize="none"
              keyboardType="email-address"
              style={[
                styles.inlineInput,
                {
                  borderColor: pal.line,
                  backgroundColor: pal.sunk,
                  color: pal.ink,
                  width: '100%',
                  marginBottom: 10,
                },
              ]}
            />
            <TouchableOpacity
              onPress={handleFindSendRecipient}
              disabled={Boolean(actionLoading) || fundsActionBusy}
              style={[s.outlineBtn, { borderColor: pal.line, marginBottom: 12 }]}
            >
              <Text style={{ color: pal.ink, fontWeight: '600', textAlign: 'center' }}>Find member</Text>
            </TouchableOpacity>

            {sendRecipient ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 14,
                  padding: 10,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: pal.ok,
                }}
              >
                <Ionicons name="checkmark-circle" size={18} color={pal.ok} />
                <Text style={{ color: pal.ink, flex: 1, fontSize: 14 }}>
                  {sendRecipient.displayName}
                  {sendRecipient.email ? ` · ${sendRecipient.email}` : ''}
                </Text>
              </View>
            ) : null}

            <View
              style={[
                styles.modalInputRow,
                { borderColor: pal.line, backgroundColor: pal.sunk, marginBottom: 16 },
              ]}
            >
              <Text style={{ color: pal.ink, fontFamily: FONT.mono, fontSize: 20 }}>$</Text>
              <TextInput
                value={sendDollars}
                onChangeText={(v) => {
                  setSendModalError('');
                  setSendDollars(v);
                }}
                keyboardType="decimal-pad"
                placeholder="1.00"
                placeholderTextColor={pal.faint}
                style={{ flex: 1, color: pal.ink, fontFamily: FONT.mono, fontSize: 22, paddingVertical: 10 }}
              />
            </View>

            <View style={{ flexDirection: 'row', gap: 10 }}>
              <TouchableOpacity
                onPress={closeSendModal}
                style={[s.outlineBtn, { borderColor: pal.line, flex: 1 }]}
                disabled={actionLoading === 'send'}
              >
                <Text style={[s.kicker, { color: pal.mute, textAlign: 'center' }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSendToUser}
                disabled={Boolean(actionLoading) || fundsActionBusy || !sendRecipient}
                style={[
                  styles.compactBtn,
                  {
                    flex: 1,
                    backgroundColor: accent,
                    opacity: !sendRecipient || actionLoading ? 0.55 : 1,
                  },
                ]}
              >
                {actionLoading === 'send' ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={{ color: '#fff', fontWeight: '600', textAlign: 'center' }}>Send</Text>
                )}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={withdrawModalOpen}
        transparent
        animationType="fade"
        onRequestClose={closeWithdrawModal}
      >
        <Pressable style={styles.modalBackdrop} onPress={closeWithdrawModal}>
          <Pressable
            style={[styles.modalSheet, { backgroundColor: pal.page, borderColor: pal.line, borderLeftColor: accent }]}
            onPress={(e) => e.stopPropagation?.()}
          >
            <View style={[styles.modalHeader, { borderBottomColor: accent }]}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={[s.label, { color: pal.mute, marginBottom: 4 }]}>
                  {withdrawStep === 'amount' ? 'Step 1 of 2 · Amount' : 'Step 2 of 2 · Confirm'}
                </Text>
                <Text style={[styles.modalTitle, { color: pal.ink }]}>Withdraw</Text>
              </View>
              <TouchableOpacity
                onPress={closeWithdrawModal}
                disabled={actionLoading === 'withdraw'}
                style={[styles.modalClose, { borderColor: pal.line }]}
              >
                <Ionicons name="close" size={18} color={pal.ink} />
              </TouchableOpacity>
            </View>

            {withdrawModalError ? (
              <View style={[styles.banner, { backgroundColor: pal.alert, marginBottom: 12 }]}>
                <Text style={{ color: '#fff', fontSize: 14 }}>{withdrawModalError}</Text>
              </View>
            ) : null}

            {withdrawStep === 'amount' ? (
              <View style={{ gap: 12 }}>
                <Text style={{ color: pal.mute, fontSize: 15, lineHeight: 22 }}>
                  How much do you want to send to your Stripe Connect balance?
                </Text>
                <Text style={[s.label, { color: pal.mute }]}>
                  Available {formatFundsCents(payoutBalanceCents)}
                </Text>
                <View
                  style={[
                    styles.modalInputRow,
                    { borderColor: pal.line, backgroundColor: pal.sunk },
                  ]}
                >
                  <Text style={{ color: pal.ink, fontFamily: FONT.mono, fontSize: 20 }}>$</Text>
                  <TextInput
                    value={withdrawDollars}
                    onChangeText={(v) => {
                      setWithdrawModalError('');
                      setWithdrawDollars(v);
                    }}
                    keyboardType="decimal-pad"
                    placeholder="1.00"
                    placeholderTextColor={pal.faint}
                    style={{ flex: 1, color: pal.ink, fontFamily: FONT.mono, fontSize: 22, paddingVertical: 10 }}
                    autoFocus={Platform.OS === 'web'}
                  />
                </View>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <TouchableOpacity
                    onPress={closeWithdrawModal}
                    style={[s.outlineBtn, { borderColor: pal.line, flex: 1 }]}
                  >
                    <Text style={[s.kicker, { color: pal.mute, textAlign: 'center' }]}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={goWithdrawConfirm}
                    style={[
                      styles.modalPrimary,
                      { backgroundColor: accent, flex: 1.4 },
                    ]}
                  >
                    <Text style={{ color: '#fff', fontWeight: '600', textAlign: 'center' }}>
                      Continue
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <View style={{ gap: 12 }}>
                <Text style={{ color: pal.mute, fontSize: 15, lineHeight: 22 }}>
                  Confirm you want to withdraw this amount. This moves funds from your Wallet
                  to Stripe Connect and cannot be undone from here.
                </Text>
                <View
                  style={[
                    styles.modalBreakdown,
                    { borderColor: pal.line, borderLeftColor: accent, backgroundColor: pal.surf },
                  ]}
                >
                  <View style={styles.modalBreakRow}>
                    <Text style={{ color: pal.mute, fontSize: 15 }}>Amount</Text>
                    <Text style={{ color: pal.ink, fontFamily: FONT.mono, fontSize: 16, fontWeight: '600' }}>
                      {formatFundsCents(parseWithdrawCents())}
                    </Text>
                  </View>
                  <View style={[styles.modalBreakRow, { borderTopWidth: 1, borderTopColor: pal.lineSoft }]}>
                    <Text style={{ color: pal.mute, fontSize: 15 }}>Destination</Text>
                    <Text style={{ color: pal.ink, fontSize: 14, fontWeight: '600' }}>Stripe Connect</Text>
                  </View>
                </View>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <TouchableOpacity
                    onPress={() => {
                      setWithdrawModalError('');
                      setWithdrawStep('amount');
                    }}
                    disabled={actionLoading === 'withdraw'}
                    style={[s.outlineBtn, { borderColor: pal.line, flex: 1 }]}
                  >
                    <Text style={[s.kicker, { color: pal.mute, textAlign: 'center' }]}>Back</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleWithdrawToConnect}
                    disabled={actionLoading === 'withdraw'}
                    style={[
                      styles.modalPrimary,
                      {
                        backgroundColor: accent,
                        flex: 1.4,
                        opacity: actionLoading === 'withdraw' ? 0.55 : 1,
                      },
                    ]}
                  >
                    {actionLoading === 'withdraw' ? (
                      <ActivityIndicator color="#fff" />
                    ) : (
                      <Text style={{ color: '#fff', fontWeight: '600', textAlign: 'center' }}>
                        Confirm withdraw
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const s = StyleSheet.create({
  label: { fontFamily: FONT.mono, fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase' },
  meta: { fontFamily: FONT.mono, fontSize: 11.5 },
  kicker: { fontFamily: FONT.mono, fontSize: 12.5, letterSpacing: 0.7, textTransform: 'uppercase' },
  hero: { fontSize: 36, fontWeight: '700', letterSpacing: -0.6 },
  card: { borderWidth: 1, borderRadius: 12, padding: 18, marginBottom: 16 },
  outlineBtn: { paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1 },
  colH: { fontFamily: FONT.mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase' },
});

const styles = StyleSheet.create({
  page: { flex: 1 },
  pageInner: {
    padding: 24,
    paddingBottom: 48,
    maxWidth: 980,
    width: '100%',
    alignSelf: 'center',
  },
  banner: {
    padding: 12,
    borderRadius: 9,
    marginBottom: 14,
  },
  hubTabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    marginBottom: 20,
    gap: 4,
  },
  hubTab: {
    paddingVertical: 10,
    paddingHorizontal: 4,
    marginRight: 20,
  },
  heroRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    marginBottom: 20,
    paddingBottom: 18,
    borderBottomWidth: 2,
  },
  accentPanel: {
    flex: 1,
    minWidth: 280,
    maxWidth: 380,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderLeftWidth: 3,
    overflow: 'hidden',
  },
  accentStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  accentLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderTopWidth: 1,
  },
  plusBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  inlinePanel: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  inlineInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 16,
    fontFamily: FONT.mono,
    minWidth: 110,
  },
  compactBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalSheet: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 12,
    borderWidth: 1,
    borderLeftWidth: 3,
    padding: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
    paddingBottom: 14,
    borderBottomWidth: 2,
  },
  modalTitle: {
    fontFamily: FONT.sans,
    fontSize: 28,
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  modalClose: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  modalPrimary: {
    borderRadius: 9,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  modalBreakdown: {
    borderWidth: 1,
    borderLeftWidth: 3,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  modalBreakRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    gap: 12,
  },
  stripeMark: {
    height: 18,
    justifyContent: 'center',
  },
  tableHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 18,
    paddingVertical: 14,
    gap: 12,
  },
  tierHead: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 18,
    paddingBottom: 8,
    borderTopWidth: 1,
    paddingTop: 10,
  },
  tierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderTopWidth: 1,
  },
});
